/**
 * Setup for Classic or The Deep (docs/spec/07 §2): players, start cave (A E I M), level, START,
 * with a picture of the chosen cave at the chosen level that uncovers whenever it changes.
 */

import { caveSet, findCave, levelOf } from '../../caves';
import type { CaveSet, CaveSetId } from '../../caves/types';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput, SceneHost } from '../scene';
import { UiScene } from '../kit/base';
import { drawButton, drawPanel } from '../kit/chrome';
import type { Rect } from '../kit/menuModel';
import { inRect, wrapIndex } from '../kit/menuModel';
import { drawSpinner } from '../kit/spinner';
import { wrap } from '../kit/text';
import { drawCaveThumb } from '../kit/thumbs';
import { drawUncover } from '../kit/uncover';
import { ACCENT, FAINT, HEADING, INK, MUTED, SELECTED } from '../kit/theme';
import type { SetupChoice, SetupRow } from './setupModel';
import { chooseValue, chosenIndex, DEFAULT_CHOICE, rowValues, SETUP_ROWS, stepChoice, validChoice } from './setupModel';

/** The last choices per set, for the page session. */
const memory: Record<CaveSetId, SetupChoice> = { classic: DEFAULT_CHOICE, deep: DEFAULT_CHOICE };

const COL_X = 22;
const ROW_Y: Record<SetupRow, number> = { players: 30, cave: 60, level: 90, start: 122 };
const HEADINGS: Record<SetupRow, string> = { players: 'PLAYERS', cave: 'START CAVE', level: 'LEVEL', start: '' };
const START: Rect = { x: 14, y: 117, w: 118, h: 17 };
const THUMB_PANEL: Rect = { x: 146, y: 26, w: 166, h: 94 };
const THUMB: Rect = { x: 149, y: 29, w: 160, h: 88 };
const CHIP_STEP = 20;

function chipRect(row: SetupRow, i: number): Rect {
  return { x: COL_X + i * CHIP_STEP - 5, y: ROW_Y[row] + 9, w: 18, h: 13 };
}

function rowRectOf(row: SetupRow): Rect {
  return row === 'start' ? START : { x: 0, y: ROW_Y[row] - 4, w: 140, h: 28 };
}

export class SetupScene extends UiScene {
  protected readonly heading: string;
  private readonly set: CaveSet;
  private choice: SetupChoice;
  private row = 3;
  private changedAt = 0;
  private seed = 1;

  constructor(host: SceneHost, private readonly setId: CaveSetId) {
    super(host);
    this.heading = setId === 'deep' ? 'THE DEEP' : 'PLAY CLASSIC';
    this.set = caveSet(setId);
    this.choice = validChoice(memory[setId], this.set);
  }

  private get rowId(): SetupRow {
    return SETUP_ROWS[this.row] ?? 'start';
  }

  protected override describe(): string {
    return `${this.heading}. ${this.rowLabel()}`;
  }

  protected override hint(): string {
    return `◄► CHANGE   ▲▼ MOVE   ${this.words().ok} START`;
  }

  private rowLabel(): string {
    const c = this.choice;
    const r = this.rowId;
    if (r === 'players') return `Players ${c.players}`;
    if (r === 'cave') return `Start cave ${c.cave}, ${findCave(this.set, c.cave)?.name ?? ''}`;
    if (r === 'level') return `Level ${c.level}`;
    return 'Start';
  }

  protected paint(screen: Screen): void {
    for (const row of SETUP_ROWS) this.paintRow(screen, row);
    drawButton(screen, START, 'START', this.rowId === 'start' ? 'selected' : 'idle', this.ms);
    const def = findCave(this.set, this.choice.cave);
    drawPanel(screen, THUMB_PANEL);
    if (!def) return;
    drawCaveThumb(screen, def, this.choice.level, THUMB);
    drawUncover(screen, THUMB, this.ms - this.changedAt, this.seed, def.colors);
    screen.text(def.name, THUMB_PANEL.x + THUMB_PANEL.w / 2, 126, SELECTED, 'center');
    wrap(def.hint, 20).slice(0, 3).forEach((line, i) => {
      screen.text(line, THUMB_PANEL.x + THUMB_PANEL.w / 2, 138 + i * 10, MUTED, 'center');
    });
    const lv = levelOf(def, this.choice.level);
    const info: [string, string][] = [
      ['NEED', `${lv.diamonds} ◆`],
      ['TIME', `${lv.time} S`],
      ['VALUE', `${def.diamondValue} / ${def.extraValue}`],
    ];
    info.forEach(([k, v], i) => {
      screen.text(k, COL_X - 8, 146 + i * 10, FAINT);
      screen.text(v, COL_X + 40, 146 + i * 10, ACCENT);
    });
  }

  private paintRow(screen: Screen, row: SetupRow): void {
    if (row === 'start') return;
    const on = this.rowId === row;
    const y = ROW_Y[row];
    if (on) drawSpinner(screen, 6, y, this.ms);
    screen.text(HEADINGS[row], COL_X - 2, y, on ? SELECTED : HEADING);
    const chosen = chosenIndex(row, this.choice);
    rowValues(row, this.set).forEach((v, i) => {
      const r = chipRect(row, i);
      const picked = i === chosen;
      if (picked) screen.box(r.x, r.y, r.w, r.h, on ? SELECTED : MUTED);
      const colour = !v.available ? FAINT : picked ? (on ? SELECTED : INK) : MUTED;
      screen.text(v.label, r.x + r.w / 2, r.y + 3, colour, 'center');
    });
  }

  protected onAction(a: MenuAction): void {
    if (a === 'up' || a === 'down') {
      this.row = wrapIndex(this.row + (a === 'up' ? -1 : 1), SETUP_ROWS.length);
      this.sound('move');
      this.say(this.rowLabel());
    } else if (a === 'left' || a === 'right') {
      this.apply(stepChoice(this.rowId, this.choice, a === 'left' ? -1 : 1, this.set));
    } else if (a === 'confirm') this.start();
  }

  /** Apply a new choice: remember it, sound it, and uncover the picture if the cave changed. */
  private apply(next: SetupChoice): void {
    const before = this.choice;
    if (next.players === before.players && next.cave === before.cave && next.level === before.level) {
      this.sound('error');
      return;
    }
    this.choice = next;
    memory[this.setId] = next;
    this.sound('move');
    this.say(this.rowLabel());
    if (next.cave !== before.cave || next.level !== before.level) {
      this.changedAt = this.ms;
      this.seed++;
    }
  }

  private start(): void {
    this.sound('select');
    const c = this.choice;
    this.host.go({ to: 'play', request: { mode: this.setId, players: c.players, startCave: c.cave, level: c.level } });
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'wheel') return this.apply(stepChoice(this.rowId, this.choice, p.delta, this.set));
    const row = SETUP_ROWS.findIndex((r) => inRect(rowRectOf(r), p.x, p.y));
    if (row < 0) return;
    if (row !== this.row) {
      this.row = row;
      if (p.kind === 'move') this.sound('move');
    }
    if (p.kind !== 'down' || p.button !== 0) return;
    const id = this.rowId;
    if (id === 'start') return this.start();
    const i = rowValues(id, this.set).findIndex((_, k) => inRect(chipRect(id, k), p.x, p.y));
    if (i >= 0 && i !== chosenIndex(id, this.choice)) this.apply(chooseValue(id, this.choice, i, this.set));
  }
}
